import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Eye, EyeOff, ArrowLeft, Building2, Truck } from "lucide-react";
import { TafLogo } from "@/components/Brand";
import { toast } from "@/components/ui/sonner";
import { COMPANY_META, setCompany, getCompany } from "@/lib/company";

const ICONS = { taf: Building2, vavlis: Truck };

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [company, setComp] = useState("taf");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const ge = p.get("google_error");
    if (ge) {
      toast.error(ge);
      window.history.replaceState(null, "", "/login");
    }
  }, []);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (user) return <Navigate to="/" replace />;

  const pick = (id) => {
    setCompany(id);
    setComp(id);
  };

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await login(username, password, company || getCompany());
      toast.success("Καλώς ήρθατε");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F5F1] p-6" data-testid="company-picker">
        <div className="w-full max-w-3xl">
          <div className="text-center">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C86235]">Επιλογή Εταιρείας</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#45423C] mt-3">Σε ποια εταιρεία θα δουλέψεις;</h1>
            <p className="text-[#6F6A60] text-sm mt-3">Κάθε εταιρεία έχει ξεχωριστά δεδομένα, προσωπικό και οικονομικά.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 mt-10">
            {Object.values(COMPANY_META).map((c) => {
              const Icon = ICONS[c.id] || Building2;
              const dark = c.id === "vavlis" ? "#1B2E3B" : "#45423C";
              const accent = c.id === "vavlis" ? "#D08A3C" : "#C86235";
              return (
                <button key={c.id} data-testid={`pick-company-${c.id}`} onClick={() => pick(c.id)}
                  className="group text-left rounded-2xl border border-[#E3DED4] bg-white p-7 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:border-[#B9AE9A]">
                  <span className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: dark }}>
                    <Icon className="h-6 w-6" style={{ color: accent }} />
                  </span>
                  <h2 className="font-serif text-2xl mt-5" style={{ color: dark }}>{c.name}</h2>
                  <p className="text-sm text-[#6F6A60] mt-1.5">{c.tagline}</p>
                  <span className="mt-6 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest"
                    style={{ color: accent }}>
                    Είσοδος →
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const meta = COMPANY_META[company];

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F7F5F1]">
      <div
        className="hidden lg:flex flex-col justify-between p-12 relative bg-[#45423C]"
        style={{
          backgroundImage:
            company === "vavlis"
              ? "linear-gradient(rgba(27,46,59,0.82),rgba(27,46,59,0.92)), url('https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940')"
              : "linear-gradient(rgba(17,30,46,0.72),rgba(17,30,46,0.86)), url('https://images.pexels.com/photos/12715490/pexels-photo-12715490.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="flex items-center gap-3">
          {company === "taf" ? (
            <div>
              <TafLogo height={54} light />
              <div className="text-[11px] uppercase tracking-[0.3em] text-white/70 mt-2">Διοίκηση & Οικονομικά</div>
            </div>
          ) : (
            <div>
              <div className="font-serif text-3xl text-white">VAVLIS <span className="opacity-60">oe</span></div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-white/70 mt-2">Χονδρική · Διοίκηση</div>
            </div>
          )}
        </div>
        <div>
          <h1 className="font-serif text-4xl xl:text-5xl text-white leading-tight">
            {company === "vavlis" ? (
              <>Χονδρική με τάξη,<br /> έλεγχο και ταχύτητα.</>
            ) : (
              <>Μεσογειακή φιλοξενία,<br /> αβίαστη διαχείριση.</>
            )}
          </h1>
          <p className="text-white/70 mt-4 max-w-md">
            {company === "vavlis"
              ? "Προσωπικό, μισθοδοσία, έξοδα και οικονομικά για το χονδρεμπόριο καθαριστικών, χάρτου και συσκευασίας."
              : "Διαχειριστείτε μισθοδοσία, tips, ώρες εργασίας και έξοδα για κάθε βίλα στην Κω — όλα σε Ευρώ, όλα σε ένα μέρος."}
          </p>
        </div>
        <div className="text-white/40 text-xs">© {new Date().getFullYear()} {meta.name}</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F1EDE6] px-3 py-1.5" data-testid="login-company-badge">
            <span className="h-2 w-2 rounded-full bg-[#C86235]" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-[#45423C]">{meta.name}</span>
          </div>
          <h2 className="font-serif text-3xl text-[#45423C] mt-4">Σύνδεση</h2>
          <p className="text-[#6F6A60] mt-1 text-sm">Με όνομα χρήστη και κωδικό.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Όνομα χρήστη</label>
              <input
                data-testid="login-username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="mt-1.5 w-full rounded-xl border border-[#E3DED4] bg-white px-4 py-3 text-sm shadow-sm outline-none transition-colors focus:border-[#B9AE9A] focus:ring-2 focus:ring-[#B9AE9A]/20"
                placeholder="andreas"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Κωδικός</label>
              <div className="relative mt-1.5">
                <input
                  data-testid="login-password"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full rounded-xl border border-[#E3DED4] bg-white px-4 py-3 text-sm outline-none focus:border-[#45423C] transition-colors pr-11"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6F6A60]"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <button
              data-testid="login-submit"
              disabled={busy}
              className="w-full rounded-xl bg-[#45423C] text-white py-3 text-sm font-semibold hover:bg-[#56524A] transition-colors disabled:opacity-60"
            >
              {busy ? "Σύνδεση…" : "Σύνδεση"}
            </button>
          </form>

          <div className="mt-5">
            <div className="relative flex items-center gap-3">
              <div className="h-px flex-1 bg-[#E3DED4]" />
              <span className="text-[11px] uppercase tracking-wider text-[#6F6A60]">ή</span>
              <div className="h-px flex-1 bg-[#E3DED4]" />
            </div>
            <button
              type="button"
              data-testid="google-login-btn"
              onClick={googleLogin}
              className="mt-4 w-full rounded-xl border border-[#E3DED4] bg-white py-3 text-sm font-semibold text-[#45423C] hover:border-[#B9AE9A] hover:bg-[#B9AE9A]/5 transition-colors flex items-center justify-center gap-2.5"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
              </svg>
              Σύνδεση με Google
            </button>
            <p className="text-[11px] text-[#8A8577] mt-2 text-center">Μόνο για λογαριασμούς με συνδεδεμένο email Google.</p>
          </div>

        </div>
      </div>
    </div>
  );
}
